// setStreamToken.js
'use strict';
import setIsPrivateStreamUnlocked from '../action/setIsPrivateStreamUnlocked.js';
import setLivestreamPaymentTimer from '../action/setLivestreamPaymentTimer.js';
import updateLiveStreamPreset from '../action/updateLiveStreamPreset.js';

import getLiveStreamData from '../selector/getLiveStreamData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getOperationData from '../selector/getOperationData.js';
import getExternalData from '../selector/getExternalData.js';

import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import {
  PRESET_SD,
  PRECHARGE_SECONDS,
  livestreamPaymentVia as livestreamPaymentViaConstant,
} from '../resource/liveStreamConstants.js';

/**
 * Set stream token
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {string} {token} - token to gain access to the stream.
 * @param {string} {byteplusToken} - byteplus token to gain access to byteplus rtc room
 * @param {string} {byteplusTokenNbf} - byteplus token's nbf
 * @param {string} {byteplusTokenExp} - byteplus token's exp
 * @param {LivestreamTokenVia} {via} - token coming from
 * @return {Promise} Action promise.
 */
const setStreamToken =
  ({
    streamId,
    token,
    byteplusToken,
    byteplusTokenNbf,
    byteplusTokenExp,
    via,
  } = {}) =>
  async (dispatch, getState) => {
    if (!token && !byteplusToken) {
      return dispatch({ type: '' });
    }
    const decode = (await import('jwt-decode')).jwtDecode;
    const decodedToken = decode(token);
    const timestampOffsetSeconds = getTimestampOffset(
      getState(),
      Accuracy.SECOND
    );
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    if (
      (!decodedToken?.exp || decodedToken?.exp <= now) &&
      (!byteplusTokenExp || byteplusTokenExp <= now)
    ) {
      return dispatch({ type: '' });
    }
    dispatch(
      setIsPrivateStreamUnlocked({
        streamId,
        isPrivateStreamUnlocked: true,
      })
    );
    const setRemoveStreamTokenTimer = (
      await import('../action/setRemoveStreamTokenTimer.js')
    ).default;
    dispatch(
      setRemoveStreamTokenTimer({
        streamId,
        tokenExpireUnixTimestamp: decodedToken?.exp || byteplusTokenExp,
      })
    );
    const performingId = getLiveStreamData(
      getState(),
      [streamId, 'privateShow'],
      'performingId'
    );
    const isLivestreamAutoPaymentEnabled = getOperationData(
      getState(),
      ['liveStream', 'autoPayment', streamId],
      'isEnabled'
    );
    if (!performingId && isLivestreamAutoPaymentEnabled) {
      dispatch(
        setLivestreamPaymentTimer({
          streamId,
          paymentTimestamp:
            (decodedToken?.exp || byteplusTokenExp) - PRECHARGE_SECONDS,
          via: livestreamPaymentViaConstant.AUTO,
        })
      );
    }
    const playerStatus = getLiveStreamData(
      getState(),
      [streamId],
      'playerStatus'
    );
    const streamStatus = getLiveStreamData(getState(), [streamId], 'status');
    trackMixpanel({
      type: EventTypes.LIVESTREAM_TOKEN_GOT,
      payload: {
        streamId,
        status: streamStatus,
        tokenNbf: decodedToken?.nbf,
        tokenExp: decodedToken?.exp,
        byteplusTokenNbf,
        byteplusTokenExp,
        playerStatus: playerStatus,
        preset: PRESET_SD,
        clientTimestamp: getExternalData(getState(), ['timestamp'], 'client'),
        serverTimestamp: getExternalData(getState(), ['timestamp'], 'server'),
        via,
      },
    });
    return dispatch(
      updateLiveStreamPreset({
        streamId,
        preset: PRESET_SD,
        pusherPreset: PRESET_SD,
        streamToken: token,
        decodedToken,
        byteplusToken,
        byteplusTokenNbf,
        byteplusTokenExp,
      })
    );
  };

export default setStreamToken;
